import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, message, Card, Typography, Space } from 'antd';
import { MailOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import './ForgotPassword.css';
import { HERO_BACKGROUND_MP4 } from '../constants/publicAssets';

const { Title, Text } = Typography;

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [bgVideoBroken, setBgVideoBroken] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // 确保视频在移动端自动播放
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      // 设置视频属性以支持自动播放
      video.muted = true;
      video.playsInline = true;
      
      // 尝试播放视频
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // 播放成功，隐藏播放控件
            video.style.pointerEvents = 'none';
          })
          .catch((error) => {
            console.log('自动播放失败，可能需要用户交互:', error);
            // 如果自动播放失败，在用户点击页面时尝试播放
            const handleInteraction = () => {
              video.play().catch(err => console.log('播放失败:', err));
              document.removeEventListener('touchstart', handleInteraction);
              document.removeEventListener('click', handleInteraction);
            };
            document.addEventListener('touchstart', handleInteraction, { once: true });
            document.addEventListener('click', handleInteraction, { once: true });
          });
      }
    }
  }, []);

  const handleSubmit = async (values: { teamCode: string; email: string }) => {
    setLoading(true);
    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          teamCode: values.teamCode,
          email: values.email,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        message.success(data.message || '重置密码链接已发送到您的邮箱，请查收');
        form.resetFields();
      } else {
        message.error(data.error || '发送失败，请稍后重试');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      message.error('发送失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-password-container">
      {/* 视频背景 */}
      <video 
        ref={videoRef}
        className="forgot-password-video-background" 
        autoPlay 
        muted 
        playsInline
        loop
        preload="auto"
        disablePictureInPicture
        controlsList="nodownload nofullscreen noremoteplayback"
        onError={() => setBgVideoBroken(true)}
        style={{
          pointerEvents: 'none',
          display: bgVideoBroken ? 'none' : undefined
        } as React.CSSProperties}
        {...({
          'webkit-playsinline': true,
          'x5-playsinline': true,
          'x5-video-player-type': 'h5',
          ...(true ? { 'x5-video-player-fullscreen': 'true' } : {})
        } as any)}
      >
        <source src={HERO_BACKGROUND_MP4} type="video/mp4" />
        您的浏览器不支持视频播放。
      </video>
      
      {/* 视频遮罩层 */}
      <div className="forgot-password-video-overlay"></div>
      
      <Card className="forgot-password-card">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div className="forgot-password-header">
            <Title level={3}>忘记密码</Title>
            <Text type="secondary">请输入您的邮箱地址，我们将发送重置密码链接</Text>
          </div>

          <Form
            form={form}
            name="forgot-password"
            onFinish={handleSubmit}
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="teamCode"
              label="团队编码"
              rules={[{ required: true, message: '请输入团队编码!' }]}
            >
              <Input placeholder="与登录时使用的团队编码一致" autoComplete="off" />
            </Form.Item>

            <Form.Item
              name="email"
              label="邮箱地址"
              rules={[
                { required: true, message: '请输入邮箱地址!' },
                { type: 'email', message: '请输入有效的邮箱地址!' }
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="请输入您的邮箱地址"
                autoComplete="email"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
              >
                {loading ? '发送中...' : '发送重置链接'}
              </Button>
            </Form.Item>
          </Form>

          <div className="forgot-password-footer">
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/')}
              style={{ padding: 0 }}
            >
              返回登录
            </Button>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default ForgotPassword;

